<template>
  <v-container class="notifications" fluid>
    <div class="d-flex align-center my-6">
      <div>
        <h2>Notifications</h2>
        <div class="text-body-2 grey--text text--darken-1 mt-1">
          {{ pageDescription }}
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn color="primary" depressed @click="formDrawer = true">
        <v-icon left>mdi-plus</v-icon>
        New notification
      </v-btn>
    </div>

    <v-card class="notification-history-card mx-auto mb-6" elevation="2">
      <v-card-title class="pt-6">
        <v-text-field
          v-model="historySearch"
          prepend-inner-icon="mdi-magnify"
          label="Filter current page"
          single-line
          hide-details
          dense
          outlined
          class="mr-4"
          style="max-width: 280px">
        </v-text-field>
        <v-spacer></v-spacer>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              :loading="historyLoading"
              @click="fetchHistory({ resetPage: true })"
              v-bind="attrs"
              v-on="on">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Refresh history</span>
        </v-tooltip>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="historyHeaders"
          :items="historyItems"
          :search="historySearch"
          :loading="historyLoading"
          :options.sync="historyOptions"
          :server-items-length="historyTotal"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          class="elevation-0">
          <template v-slot:item.msg_title="{ item }">
            <div class="text-truncate" style="max-width: 280px">{{ item.msg_title }}</div>
          </template>
          <template v-slot:item.audience="{ item }">
            <span>{{ formatAudience(item) }}</span>
          </template>
          <template v-slot:item.recipient_count="{ item }">
            <v-chip small outlined color="primary">
              {{ item.group_type === 'all' ? 'All' : item.recipient_count }}
            </v-chip>
          </template>
          <template v-slot:item.created_on="{ item }">
            {{ formatDateTime(item.created_on) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="viewNotification(item)">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </template>
          <template v-slot:no-data>
            <div class="py-6 text-center">
              <v-icon size="48" color="grey lighten-1">mdi-bell-outline</v-icon>
              <div class="text-body-2 grey--text mt-2">No notifications have been sent yet.</div>
              <v-btn color="primary" text class="mt-2" @click="formDrawer = true">
                <v-icon left small>mdi-plus</v-icon>
                Send your first notification
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-navigation-drawer
      v-model="formDrawer"
      right
      temporary
      fixed
      width="560"
      class="notification-form-drawer">
      <div class="d-flex flex-column" style="height: 100%">
        <!-- Drawer Header -->
        <div class="d-flex align-center px-5 py-4 drawer-header">
          <span class="text-h6">New notification</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeForm" :disabled="loading">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>

        <!-- Drawer Body -->
        <div class="flex-grow-1 px-5 py-4 drawer-body">
          <v-form ref="form" lazy-validation v-model="valid">
            <v-text-field
              label="Notification title"
              v-model="notification.title"
              dense
              required
              :rules="titleRules"
              validate-on-blur
              outlined
              hide-details="auto"
              counter="100"
              class="mb-3">
            </v-text-field>

            <v-textarea
              label="Notification body"
              v-model="notification.message"
              dense
              required
              :rules="messageRules"
              validate-on-blur
              outlined
              hide-details="auto"
              counter="500"
              rows="5"
              auto-grow>
            </v-textarea>

            <!-- Audience -->
            <div class="mt-6 mb-2">
              <div class="d-flex align-center">
                <div class="text-subtitle-1 font-weight-medium">Audience</div>
                <v-spacer></v-spacer>
                <v-checkbox
                  v-model="sendToAll"
                  label="Send to all"
                  color="primary"
                  dense
                  hide-details
                  class="mt-0 pt-0"
                  @change="onSendToAllChange">
                </v-checkbox>
              </div>
              <div class="text-caption grey--text text--darken-1">
                {{ audienceCaption }}
              </div>
            </div>
            <v-row dense v-if="!sendToAll">
              <v-col v-if="!storesOnlyAudience" cols="12" sm="6">
                <v-autocomplete
                  label="Sources"
                  v-model="selectedSources"
                  :items="sources"
                  :rules="audienceRules"
                  :loading="loadingSources"
                  :disabled="loadingSources"
                  item-text="text"
                  item-value="value"
                  return-object
                  dense
                  outlined
                  hide-details="auto"
                  multiple
                  small-chips
                  deletable-chips
                  clearable
                  @change="onTargetingChange">
                  <template v-slot:prepend-item>
                    <v-list-item ripple @click="toggleSelectAll('selectedSources', 'sources')">
                      <v-list-item-action>
                        <v-icon :color="selectedSources.length > 0 ? 'primary' : ''">
                          {{ selectAllSourcesIcon }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>Select All</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      v-if="selectedSources.length < 3"
                      small
                      close
                      class="audience-chip"
                      @click:close="removeTargetItem('selectedSources', index)">
                      <span class="d-inline-block text-truncate audience-chip-label">{{ item.text }}</span>
                    </v-chip>
                    <v-chip
                      v-else-if="index === 0"
                      small
                      label
                      color="primary"
                      outlined>
                      {{ selectedSources.length }} selected
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col v-if="!storesOnlyAudience" cols="12" sm="6">
                <v-autocomplete
                  label="Store Groups"
                  v-model="selectedStoreGroups"
                  :items="storeGroups"
                  :rules="audienceRules"
                  :loading="loadingStoreGroups"
                  :disabled="loadingStoreGroups"
                  item-text="text"
                  item-value="value"
                  return-object
                  dense
                  outlined
                  hide-details="auto"
                  multiple
                  small-chips
                  deletable-chips
                  clearable
                  @change="onTargetingChange">
                  <template v-slot:prepend-item>
                    <v-list-item ripple @click="toggleSelectAll('selectedStoreGroups', 'storeGroups')">
                      <v-list-item-action>
                        <v-icon :color="selectedStoreGroups.length > 0 ? 'primary' : ''">
                          {{ selectAllIcon }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>Select All</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      v-if="selectedStoreGroups.length < 3"
                      small
                      close
                      class="audience-chip"
                      @click:close="removeTargetItem('selectedStoreGroups', index)">
                      <span class="d-inline-block text-truncate audience-chip-label">{{ item.text }}</span>
                    </v-chip>
                    <v-chip
                      v-else-if="index === 0"
                      small
                      label
                      color="primary"
                      outlined>
                      {{ selectedStoreGroups.length }} selected
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" :sm="storesOnlyAudience ? 12 : 6">
                <v-autocomplete
                  label="Stores"
                  v-model="selectedStores"
                  :items="stores"
                  :rules="audienceRules"
                  :loading="loadingStores"
                  :disabled="loadingStores"
                  item-text="text"
                  item-value="value"
                  return-object
                  dense
                  outlined
                  hide-details="auto"
                  multiple
                  small-chips
                  deletable-chips
                  clearable
                  @change="onTargetingChange">
                  <template v-slot:prepend-item>
                    <v-list-item ripple @click="toggleSelectAll('selectedStores', 'stores')">
                      <v-list-item-action>
                        <v-icon :color="selectedStores.length > 0 ? 'primary' : ''">
                          {{ selectAllStoresIcon }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>Select All</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      v-if="selectedStores.length < 3"
                      small
                      close
                      class="audience-chip"
                      @click:close="removeTargetItem('selectedStores', index)">
                      <span class="d-inline-block text-truncate audience-chip-label">{{ item.text }}</span>
                    </v-chip>
                    <v-chip
                      v-else-if="index === 0"
                      small
                      label
                      color="primary"
                      outlined>
                      {{ selectedStores.length }} selected
                    </v-chip>
                  </template>
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item v-bind="attrs" color="primary" v-on="on">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.subtitle">
                          Store #{{ item.subtitle }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col v-if="!storesOnlyAudience" cols="12" sm="6">
                <v-autocomplete
                  label="Departments"
                  v-model="selectedDepartments"
                  :items="departments"
                  :rules="audienceRules"
                  :loading="loadingDepartments"
                  :disabled="loadingDepartments"
                  item-text="name"
                  item-value="id"
                  return-object
                  dense
                  outlined
                  hide-details="auto"
                  multiple
                  small-chips
                  deletable-chips
                  clearable
                  @change="onTargetingChange">
                  <template v-slot:prepend-item>
                    <v-list-item ripple @click="toggleSelectAll('selectedDepartments', 'departments')">
                      <v-list-item-action>
                        <v-icon :color="selectedDepartments.length > 0 ? 'primary' : ''">
                          {{ selectAllDepartmentsIcon }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>Select All</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      v-if="selectedDepartments.length < 3"
                      small
                      close
                      class="audience-chip"
                      @click:close="removeTargetItem('selectedDepartments', index)">
                      <span class="d-inline-block text-truncate audience-chip-label">{{ item.name }}</span>
                    </v-chip>
                    <v-chip
                      v-else-if="index === 0"
                      small
                      label
                      color="primary"
                      outlined>
                      {{ selectedDepartments.length }} selected
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <!-- Intersection note: parties narrow to department subscribers only -->
            <v-row dense v-if="hasParties && selectedDepartments.length > 0">
              <v-col cols="12">
                <div class="text-caption info--text">
                  Notification will go to users in these parties <strong>who are also</strong> opted into these departments.
                </div>
              </v-col>
            </v-row>
          </v-form>
        </div>

        <!-- Drawer Footer -->
        <v-divider></v-divider>
        <div class="px-5 py-3 drawer-footer">
          <div class="mb-3" style="min-height: 24px">
            <transition name="recipient-count" mode="out-in">
              <div v-if="!hasTargeting" key="empty" class="text-caption grey--text">
                Select an audience to see recipient count.
              </div>
              <div v-else key="filled" class="d-flex align-center flex-wrap">
                <v-chip
                  :color="recipientChipColor"
                  :text-color="recipientChipTextColor"
                  :outlined="recipientPreviewLoading"
                  small
                  label
                  class="font-weight-medium mr-2">
                  <v-icon left small>{{ recipientCountIcon }}</v-icon>
                  {{ recipientCountText }}
                </v-chip>
                <span class="text-caption grey--text text--darken-1">
                  Estimate based on current audience; actual delivery may vary.
                </span>
              </div>
            </transition>
          </div>

          <div class="d-flex align-center">
            <v-btn text @click="resetForm" :disabled="loading || isFormPristine">
              Reset
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn text class="mr-2" @click="closeForm" :disabled="loading">
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              depressed
              :loading="loading"
              @click="validateAndSubmit">
              Send notification
            </v-btn>
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <v-dialog v-model="confirmDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Confirm Send Notification
        </v-card-title>
        <v-card-text>
          <div class="text-body-1 mb-2">
            Are you sure you want to send this notification?
          </div>
          <v-divider class="my-3"></v-divider>
          <div class="mb-2">
            <strong>Title:</strong> {{ notification.title }}
          </div>
          <div class="mb-2">
            <strong>Message:</strong> {{ notification.message }}
          </div>
          <div v-if="selectedSources.length > 0">
            <strong>Sources:</strong> {{ selectedSources.length }} selected
          </div>
          <div v-if="selectedStoreGroups.length > 0">
            <strong>Store Groups:</strong> {{ selectedStoreGroups.length }} selected
          </div>
          <div v-if="selectedStores.length > 0">
            <strong>Stores:</strong> {{ selectedStores.length }} selected
          </div>
          <div v-if="selectedDepartments.length > 0">
            <strong>Departments:</strong> {{ selectedDepartments.map(d => d.name).join(', ') }}
          </div>
          <div v-if="recipientPreviewSupported && recipientCount !== null && !recipientPreviewError" class="mt-1">
            <strong>Estimated recipients:</strong>
            {{ recipientToAll ? 'All subscribers' : recipientCount.toLocaleString() }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="loading"
            @click="confirmSend">
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Notification Details</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="viewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="selectedNotification">
          <v-container>
            <v-row dense>
              <v-col cols="12">
                <div class="text-subtitle-2 grey--text">Title</div>
                <div class="text-body-1 mt-1">
                  {{ selectedNotification.msg_title }}
                </div>
              </v-col>
              <v-col cols="12" class="mt-4">
                <div class="text-subtitle-2 grey--text">Message</div>
                <div class="text-body-1 mt-1" style="white-space: pre-wrap">
                  {{ selectedNotification.msg_body }}
                </div>
              </v-col>
              <v-col cols="12" md="6" class="mt-4">
                <div class="text-subtitle-2 grey--text">Audience</div>
                <div class="text-body-2 mt-1">
                  {{ formatAudience(selectedNotification) }}
                </div>
              </v-col>
              <v-col cols="12" md="6" class="mt-4">
                <div class="text-subtitle-2 grey--text">Recipients</div>
                <div class="text-body-2 mt-1">
                  {{ selectedNotification.group_type === 'all' ? 'All subscribers' : (selectedNotification.recipient_count || 0).toLocaleString() }}
                </div>
              </v-col>
              <v-col cols="12" md="6" class="mt-4">
                <div class="text-subtitle-2 grey--text">Sent by</div>
                <div class="text-body-2 mt-1">
                  {{ selectedNotification.created_by || '—' }}
                </div>
              </v-col>
              <v-col cols="12" md="6" class="mt-4">
                <div class="text-subtitle-2 grey--text">Sent</div>
                <div class="text-body-2 mt-1">
                  {{ formatDateTime(selectedNotification.created_on) }}
                </div>
              </v-col>
              <v-col cols="12" class="mt-4" v-if="selectedNotification.to_group">
                <div class="text-subtitle-2 grey--text">Audience details</div>
                <div class="text-caption mt-1" style="word-break: break-all">
                  {{ selectedNotification.to_group }}
                </div>
              </v-col>
              <v-col cols="12" class="mt-4">
                <div class="text-subtitle-2 grey--text">Notification ID</div>
                <div class="text-caption mt-1 grey--text text--darken-1" style="font-family: monospace">
                  {{ selectedNotification.notification_id }}
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="snackbarTimeout" multi-line top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import notification from '@/axios/notification'
import Search from '@/axios/search'
import { userAccess } from '@/mixins/user-access'

export default {
  name: 'Notifications',
  mixins: [userAccess],
  data() {
    return {
      valid: true,
      loading: false,
      formDrawer: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      snackbarTimeout: 3000,
      notification: {
        title: '',
        message: ''
      },
      selectedSources: [],
      sources: [],
      loadingSources: false,
      selectedStoreGroups: [],
      storeGroups: [],
      loadingStoreGroups: false,
      selectedStores: [],
      stores: [],
      loadingStores: false,
      selectedDepartments: [],
      departments: [],
      loadingDepartments: false,
      sendToAll: false,
      recipientCount: null,
      recipientToAll: false,
      recipientPreviewLoading: false,
      recipientPreviewError: false,
      recipientPreviewSupported: true,
      recipientPreviewTimer: null,
      recipientPreviewSeq: 0,
      historyRefreshTimer: null,
      attemptedSubmit: false,
      titleRules: [
        v => !!v || 'Title is required',
        v => (v && v.length <= 100) || 'Title must be 100 characters or less'
      ],
      messageRules: [
        v => !!v || 'Message is required',
        v => (v && v.length <= 500) || 'Message must be 500 characters or less',
        v => (v && v.length >= 10) || 'Message must be at least 10 characters'
      ],
      audienceRules: [
        () => !this.attemptedSubmit || this.hasTargeting || this.sendToAll ||
          (this.storesOnlyAudience
            ? 'Select at least one store'
            : 'Select at least one source, store group, store, or department')
      ],
      historySearch: '',
      historyLoading: false,
      historyItems: [],
      historyTotal: 0,
      historyOptions: { page: 1, itemsPerPage: 10 },
      confirmDialog: false,
      viewDialog: false,
      selectedNotification: null,
      historyHeaders: [
        { text: 'Title', value: 'msg_title', sortable: false },
        { text: 'Audience', value: 'audience', sortable: false },
        { text: 'Recipients', value: 'recipient_count', sortable: false, align: 'center' },
        { text: 'Sent by', value: 'created_by', sortable: false },
        { text: 'Sent', value: 'created_on', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'center' }
      ]
    }
  },
  computed: {
    pageDescription() {
      const labels = { awg: 'AWG Mobile', jbg: 'JBG Mobile' }
      const label = labels[this.$auth?.tenant]
      return label
        ? `Compose and send push notifications to ${label} users.`
        : 'Compose and send push notifications to mobile app users.'
    },
    audienceCaption() {
      if (this.sendToAll) return 'Notification will be sent to all subscribed users.'
      if (this.storesOnlyAudience) return 'Required: at least one store.'
      return 'Required: at least one source, store group, store, or department.'
    },
    selectAllIcon() {
      return this.toggleAllIconFor(this.selectedStoreGroups, this.storeGroups)
    },
    selectAllSourcesIcon() {
      return this.toggleAllIconFor(this.selectedSources, this.sources)
    },
    selectAllStoresIcon() {
      return this.toggleAllIconFor(this.selectedStores, this.stores)
    },
    selectAllDepartmentsIcon() {
      return this.toggleAllIconFor(this.selectedDepartments, this.departments)
    },
    isFormPristine() {
      return !this.notification.title &&
        !this.notification.message &&
        !this.hasTargeting &&
        !this.sendToAll
    },
    hasParties() {
      return this.selectedSources.length > 0 ||
        this.selectedStoreGroups.length > 0 ||
        this.selectedStores.length > 0
    },
    hasTargeting() {
      return this.sendToAll || this.hasParties || this.selectedDepartments.length > 0
    },
    allPartyIds() {
      return [
        ...this.selectedSources.map(s => s.value),
        ...this.selectedStoreGroups.map(s => s.value),
        ...this.selectedStores.map(s => s.value)
      ]
    },
    recipientCountText() {
      if (!this.recipientPreviewSupported) return 'Recipient estimate unavailable'
      if (this.recipientPreviewLoading) return 'Estimating recipients…'
      if (this.recipientPreviewError) return 'Could not estimate recipients'
      if (this.recipientToAll) return 'All subscribed users'
      if (this.recipientCount === null) return ''
      if (this.recipientCount === 0) return 'No users match this audience'
      return `≈ ${this.recipientCount.toLocaleString()} user${this.recipientCount === 1 ? '' : 's'}`
    },
    recipientCountIcon() {
      if (this.recipientPreviewLoading) return 'mdi-progress-clock'
      if (!this.recipientPreviewSupported) return 'mdi-information-outline'
      if (this.recipientPreviewError || (this.hasTargeting && this.recipientCount === 0)) return 'mdi-alert-circle-outline'
      return 'mdi-account-group-outline'
    },
    recipientChipColor() {
      if (this.recipientPreviewError) return 'error'
      if (this.hasTargeting && this.recipientCount === 0) return 'warning'
      if (!this.recipientPreviewSupported || this.recipientPreviewLoading) return 'grey lighten-2'
      return 'primary'
    },
    recipientChipTextColor() {
      if (!this.recipientPreviewSupported || this.recipientPreviewLoading) return 'grey darken-2'
      return 'white'
    }
  },
  watch: {
    'historyOptions.page'() {
      this.fetchHistory()
    },
    'historyOptions.itemsPerPage'() {
      this.fetchHistory()
    }
  },
  methods: {
    closeForm() {
      if (this.loading) return
      this.formDrawer = false
    },
    toggleAllIconFor(selected, items) {
      if (items.length > 0 && selected.length === items.length) return 'mdi-close-box'
      if (selected.length > 0) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    toggleSelectAll(selectedKey, itemsKey) {
      this.$nextTick(() => {
        this[selectedKey] = this[selectedKey].length === this[itemsKey].length
          ? []
          : [...this[itemsKey]]
        this.onTargetingChange()
      })
    },
    removeTargetItem(key, index) {
      this[key].splice(index, 1)
      this.onTargetingChange()
    },
    async validateAndSubmit() {
      this.attemptedSubmit = true
      const isValid = this.$refs.form.validate()
      if (!isValid) {
        this.showSnackbar('error', 'Fill in the highlighted fields to continue.')
        this.$nextTick(() => {
          const el = this.$el.querySelector('.v-input.error--text')
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
        return
      }
      this.confirmDialog = true
    },
    async confirmSend() {
      this.confirmDialog = false

      try {
        this.loading = true

        const result = await this.submitNotification()

        const firstDetail = result.errorDetails?.[0]?.error
        if (result.errors === 0) {
          this.showSnackbar('success', `Notification sent successfully (${result.sent} of ${result.total}).`)
        } else if (result.sent > 0) {
          const tail = firstDetail ? ` First error: ${firstDetail}` : ''
          this.showSnackbar('warning', `Partially sent: ${result.sent} succeeded, ${result.errors} failed.${tail}`)
        } else {
          this.showSnackbar('error', firstDetail || `Failed to send notification (${result.errors} of ${result.total} failed).`)
        }

        if (result.sent > 0) {
          this.resetForm()
          this.formDrawer = false
          // History row is written by a background task on the API side; delay to let it land.
          this.historyRefreshTimer = setTimeout(() => this.fetchHistory({ resetPage: true }), 1500)
        }
      } catch (err) {
        this.showSnackbar('error', err.message || 'An error occurred')
      } finally {
        this.loading = false
      }
    },
    async submitNotification() {
      const payload = this.buildAudiencePayload({
        title: this.notification.title,
        body: this.notification.message
      })

      try {
        const response = await notification.sendNotification(payload)
        console.log('Notification sent successfully:', response)
        return { total: 1, sent: 1, errors: 0, errorDetails: [] }
      } catch (error) {
        const detail = error.response?.data?.detail || error.response?.data?.message || error.message
        console.error('Failed to send notification:', detail, error)
        return {
          total: 1,
          sent: 0,
          errors: 1,
          errorDetails: [{ target: 'notification', error: detail }]
        }
      }
    },
    buildAudiencePayload(base) {
      // Server rejects to_all combined with any other targeting field.
      if (this.sendToAll) return { ...base, to_all: true }
      const partyIds = this.allPartyIds
      const departmentIds = this.selectedDepartments.map(d => d.id)
      return {
        ...base,
        ...(partyIds.length > 0 && { upstream_party_ids: partyIds }),
        ...(departmentIds.length > 0 && { department_ids: departmentIds })
      }
    },
    onSendToAllChange() {
      if (this.sendToAll) {
        this.selectedSources = []
        this.selectedStoreGroups = []
        this.selectedStores = []
        this.selectedDepartments = []
      }
      this.onTargetingChange()
    },
    onTargetingChange() {
      this.$refs.form.validate()
      this.schedulePreview()
    },
    schedulePreview() {
      if (this.recipientPreviewTimer) clearTimeout(this.recipientPreviewTimer)
      if (!this.hasTargeting) {
        this.recipientCount = null
        this.recipientToAll = false
        this.recipientPreviewLoading = false
        this.recipientPreviewError = false
        return
      }
      this.recipientPreviewLoading = true
      this.recipientPreviewError = false
      this.recipientPreviewTimer = setTimeout(() => this.fetchRecipientPreview(), 350)
    },
    async fetchRecipientPreview() {
      const seq = ++this.recipientPreviewSeq
      const payload = this.buildAudiencePayload({
        title: this.notification.title || 'preview',
        body: this.notification.message || 'preview'
      })
      try {
        const { data } = await notification.previewRecipients(payload)
        if (seq !== this.recipientPreviewSeq) return
        this.recipientCount = typeof data?.count === 'number' ? data.count : null
        this.recipientToAll = !!data?.to_all
        this.recipientPreviewError = false
      } catch (error) {
        if (seq !== this.recipientPreviewSeq) return
        if (error?.response?.status === 404) {
          this.recipientPreviewSupported = false
        } else {
          this.recipientPreviewError = true
        }
        this.recipientCount = null
      } finally {
        if (seq === this.recipientPreviewSeq) {
          this.recipientPreviewLoading = false
        }
      }
    },
    async fetchDepartments() {
      this.loadingDepartments = true
      try {
        const options = await notification.getDepartments()
        this.departments = (options || [])
          .map(o => ({ id: o.id, name: o.name }))
          .sort((a, b) => a.name.localeCompare(b.name))
      } catch (error) {
        console.error('Failed to fetch departments:', error)
        this.showSnackbar('error', 'Failed to load departments')
      } finally {
        this.loadingDepartments = false
      }
    },
    async fetchStoreGroups() {
      await this.fetchPartiesByType({
        partyType: 'STORE_GROUP',
        target: 'storeGroups',
        loadingFlag: 'loadingStoreGroups',
        label: 'store groups'
      })
    },
    async fetchSources() {
      await this.fetchPartiesByType({
        partyType: 'DISTRIBUTION_CENTER',
        target: 'sources',
        loadingFlag: 'loadingSources',
        label: 'sources'
      })
    },
    async fetchStores() {
      await this.fetchPartiesByType({
        partyType: 'STORE',
        target: 'stores',
        loadingFlag: 'loadingStores',
        label: 'stores'
      })
    },
    async fetchPartiesByType({ partyType, target, loadingFlag, label }) {
      this[loadingFlag] = true
      try {
        const { data } = await Search.customerSearch(partyType)
        if (data?.length > 0) {
          this[target] = data
            .filter(party => party.party_type === partyType)
            .map(party => ({
              text: party.party_name,
              value: party.party_id,
              subtitle: party.attributes?.WHOLESALER_STORE_NUMBER
            }))
            .sort((a, b) => a.text.localeCompare(b.text))
        }
      } catch (error) {
        console.error(`Failed to fetch ${label}:`, error)
        this.showSnackbar('error', `Failed to load ${label}`)
      } finally {
        this[loadingFlag] = false
      }
    },
    async fetchHistory({ resetPage = false } = {}) {
      if (resetPage) this.historyOptions = { ...this.historyOptions, page: 1 }
      if (this.historyLoading) return
      const { page, itemsPerPage } = this.historyOptions
      const limit = itemsPerPage
      const offset = (page - 1) * itemsPerPage
      this.historyLoading = true
      try {
        const response = await notification.getAllHistory(limit, offset)
        const body = response?.data || {}
        this.historyItems = Array.isArray(body.items) ? body.items : []
        this.historyTotal = typeof body.total === 'number' ? body.total : this.historyItems.length
      } catch (error) {
        console.error('Failed to fetch notification history:', error)
        this.historyItems = []
        this.historyTotal = 0
        this.showSnackbar('error', 'Failed to load notification history')
      } finally {
        this.historyLoading = false
      }
    },
    formatAudience(item) {
      const labels = {
        all: 'All subscribers',
        subscription: 'Subscription',
        parties_departments: 'Parties + Departments',
        departments: 'Departments',
        upstream_groups: 'Parties',
        upstream_group: 'Party',
        custom: 'Email list'
      }
      const label = labels[item.group_type] || item.group_type || '—'
      if (item.group_type === 'all') return label
      const count = this.audiencePartCount(item.to_group, item.group_type)
      return count ? `${label} · ${count}` : label
    },
    audiencePartCount(toGroup, groupType) {
      if (!toGroup) return 0
      if (groupType === 'parties_departments') {
        const parts = toGroup.split(';').reduce((acc, segment) => {
          const [, value] = segment.split('=')
          if (value) acc += value.split(',').filter(Boolean).length
          return acc
        }, 0)
        return parts
      }
      return toGroup.split(',').filter(Boolean).length
    },
    resetForm() {
      this.notification = {
        title: '',
        message: ''
      }
      this.selectedSources = []
      this.selectedStoreGroups = []
      this.selectedStores = []
      this.selectedDepartments = []
      this.sendToAll = false
      this.recipientCount = null
      this.recipientToAll = false
      this.recipientPreviewLoading = false
      this.recipientPreviewError = false
      this.attemptedSubmit = false
      if (this.recipientPreviewTimer) clearTimeout(this.recipientPreviewTimer)
      if (this.$refs.form) this.$refs.form.resetValidation()
    },
    showSnackbar(color, text, timeout) {
      this.snackbarColor = color
      this.snackbarText = text
      this.snackbarTimeout = timeout || (color === 'error' ? 8000 : color === 'warning' ? 6000 : 3000)
      this.snackbar = true
    },
    viewNotification(notification) {
      this.selectedNotification = notification
      this.viewDialog = true
    },
    formatDateTime(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    }
  },
  mounted() {
    if (!this.storesOnlyAudience) {
      this.fetchSources()
      this.fetchStoreGroups()
      this.fetchDepartments()
    }
    this.fetchStores()
    this.fetchHistory()
  },
  beforeDestroy() {
    if (this.recipientPreviewTimer) clearTimeout(this.recipientPreviewTimer)
    if (this.historyRefreshTimer) clearTimeout(this.historyRefreshTimer)
  }
}
</script>

<style scoped>
.notifications {
  max-width: 1200px;
}

.notification-history-card {
  width: 100%;
}

.notification-form-drawer {
  z-index: 7;
}

.drawer-header {
  flex-shrink: 0;
}

.drawer-body {
  overflow-y: auto;
}

.drawer-footer {
  flex-shrink: 0;
  background-color: #fafafa;
}

.recipient-count-enter-active,
.recipient-count-leave-active {
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;
}
.recipient-count-enter,
.recipient-count-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.audience-chip {
  max-width: 100%;
}
.audience-chip-label {
  max-width: 120px;
  vertical-align: middle;
}
</style>
